"use client";

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQuery } from "@apollo/client";
import { SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SheetScrollableContent } from "@/components/ui/sheet-scrollable-content";
import LanguageSwitcher from "@/components/shared/languageSelect";
import { useLanguage } from "@/context/LanguageContext";
import "@/components/styles/Form.css";
import InputFieldContainer from "@/components/shared/inputComponents/InputFieldContainer";
import {
  GET_SIMPLE_PRODUCT_DETAILS,
  UPDATE_SIMPLE_PRODUCT_PROFILES,
} from "../productQueries";
import { UPDATE_PRODUCT_CONTENT_BLOCKS, UPDATE_PRODUCT_FILES } from "../productMutations";
import { ContentBlocksManager } from "@/containers/ContentBlocks/ContentBlocksManager";
import { ContentBlocksManagerHandle } from "@/containers/ContentBlocks/types";
import { ProductFilesCard } from "./ProductFilesCard";
import {
  PRODUCT_FILE_URL,
  deriveProductFileS3Key,
} from "@/components/shared/inputComponents/ProductFileRowEditor";

const formSchema = z.object({
  product_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().min(1, "Name is required"),
        description: z.string().optional(),
        language: z.string(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter((item) => item.name?.trim() !== "");
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "At least one language profile needs to be filled",
            path: [`[${index}].name`],
          });
        });
      }
      return filteredItems;
    }),
  product_files: z
    .array(
      z.object({
        _key: z.string().optional(),
        id: z.number().optional(),
        name: z.string().min(1, "Name is required"),
        edition: z.string().optional().nullable(),
        url: z
          .string()
          .min(1, "URL is required")
          .regex(
            PRODUCT_FILE_URL,
            "Must be a sftp-furnisystems.s3.amazonaws.com/vilmers/4_PRODUCT_CATALOGUES/…pdf or …/dominari/Catalogs/…pdf URL"
          ),
        suppressed: z.boolean().optional(),
      })
    )
    .optional(),
});

interface SimpleProductFormProps {
  data: {
    id: string;
    type: string;
  };
  onDirtyChange?: (dirty: boolean) => void;
  onRequestClose?: () => void;
}

export default function SimpleProductForm({
  data,
  onDirtyChange,
  onRequestClose,
}: SimpleProductFormProps) {
  const { allLanguages, languageId } = useLanguage();
  const id = Number(data?.id);
  const managerRef = useRef<ContentBlocksManagerHandle>(null);

  // `error` matters as much as `loading` here, and is the half that was
  // missing.
  //
  // `product_files` is a nonNull list whose `includeSuppressed` arg throws on a
  // non-staff or expired token, and a throw inside a nonNull field propagates
  // up and kills the WHOLE query -- so an expired admin session does not
  // degrade the files card, it leaves the entire product unloaded. Without
  // this, the form renders its blank defaults, looks perfectly editable, and
  // Save is enabled because `loading` is false.
  const {
    data: { findUniqueProductContainer: productData } = {},
    loading: loadingProductData,
    error: productDataError,
  } = useQuery(GET_SIMPLE_PRODUCT_DETAILS, {
    variables: {
      id,
    },
    fetchPolicy: "network-only",
    skip: !id,
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: "onSubmit",
    defaultValues: {
      product_profiles: allLanguages.map((lang) => ({
        id: undefined,
        name: "",
        description: "",
        language: lang,
      })),
      product_files: [] as z.infer<typeof formSchema>["product_files"],
    },
  });

  // Both of these refetch the product details query, and the order matters.
  //
  // `updateProductContentBlocks` fires its refetch BEFORE the file write below,
  // so the response it gets back cannot contain that write -- and when it lands
  // it re-runs the hydration effect, resetting the form to the PRE-SAVE file
  // list. The edit still reaches the server (onSubmit sends the validated
  // `values` snapshot, not form state), but the field visibly snaps back to the
  // old URL afterwards, and the next Save would then send the old URL and
  // silently undo the change.
  //
  // So: await the content-blocks refetch, so it cannot land mid-write or race
  // (two in-flight copies of the same query can be deduplicated, which would
  // hand the later one the earlier one's stale result), and give the file write
  // its own awaited refetch so the LAST thing the form hydrates from is server
  // state that includes it.
  const [updateProductContentBlocks] = useMutation(
    UPDATE_PRODUCT_CONTENT_BLOCKS,
    {
      refetchQueries: [
        "GetSimpleProductDetails",
        "FindManyProductContainer",
      ],
      awaitRefetchQueries: true,
    }
  );

  const [updateProduct] = useMutation(UPDATE_SIMPLE_PRODUCT_PROFILES, {
    refetchQueries: ["FindManyProductContainer"],
  });

  const [updateProductFiles] = useMutation(UPDATE_PRODUCT_FILES, {
    refetchQueries: ["GetSimpleProductDetails"],
    awaitRefetchQueries: true,
  });

  // `updateProductFiles` is a FULL REPLACE of the hand-attached rows: the array
  // it is sent IS the desired state, so an empty one deletes every file an
  // admin ever attached by hand. The ONLY thing that ever puts existing rows
  // into form state is the reset below, and that reset is skipped whenever the
  // product query did not produce usable data (an error, a null
  // `single_product`) -- in which case the `[]` default would be submitted as
  // "delete them all".
  //
  // Nothing else catches this. The zod field is `.optional()`, so an absent
  // list is valid; and gating Save on `loading` alone is exactly what failed in
  // the page-editor incident, because `loading` is false for a query that
  // errored.
  //
  // So the flag is set ONLY inside the reset that actually populated the list,
  // and it is stamped with the product id so it self-invalidates the moment a
  // different product is opened in this same component.
  const [filesHydratedForId, setFilesHydratedForId] = useState<number | null>(
    null
  );
  const productFilesHydrated = !!id && filesHydratedForId === id;

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      await updateProduct({
        variables: {
          id,
          product_profiles: values.product_profiles.map((p) => ({
            id: p.id,
            name: p.name,
            description: p.description,
            language: p.language,
          })),
        },
      });
    } catch {
      toast.error("Failed to update product");
      return;
    }

    if (id) {
      try {
        await updateProductContentBlocks({
          variables: {
            id,
            content_blocks: managerRef.current?.getBlockIds() ?? [],
          },
        });
        await managerRef.current?.commitArrangement();
      } catch {
        toast.error("Failed to update product content blocks");
        return;
      }

      // Refuse to send a list this form never loaded. See the comment on
      // `filesHydratedForId`: sending the untouched `[]` default here would
      // delete every hand-attached file on the product. Everything above has
      // already been saved, so the message has to be explicit that the files
      // are the part that did not go through.
      if (!productFilesHydrated) {
        toast.error(
          "Product saved, but catalogue files could not be loaded and were left unchanged. Reload the product before editing its files."
        );
        return;
      }

      // Full-replace of the hand-attached (erp_managed: false) files only —
      // ERP rows are never read from form state, so they can't be sent here.
      // Two rows that differ ONLY by `?etag=` still derive the same key, so
      // they collide -- the server rejects the whole call with
      // "Duplicate product file: <key>". Caught here first so the message can
      // name the row and the non-obvious reason rather than showing a raw key.
      const productFiles = values.product_files ?? [];
      const seenKeys = new Map<string, string>();
      for (const file of productFiles) {
        const s3_key = deriveProductFileS3Key(file.url);
        const clashesWith = seenKeys.get(s3_key);
        if (clashesWith !== undefined) {
          toast.error(
            `"${file.name}" and "${clashesWith}" point at the same file (${s3_key}). ` +
              `Two rows can't share one PDF — a different ?etag= is still the same file. ` +
              `Remove one of them.`
          );
          return;
        }
        seenKeys.set(s3_key, file.name);
      }
      try {
        await updateProductFiles({
          variables: {
            productContainerId: id,
            files: productFiles.map((file) => ({
              name: file.name,
              url: file.url,
              edition: file.edition || null,
            })),
          },
        });
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to update product files");
        return;
      }
    }

    toast.success("Product updated successfully");
    onRequestClose?.();
  }

  useEffect(() => {
    if (productData?.single_product?.product_profiles) {
      form.reset({
        product_profiles: productData.single_product.product_profiles
          .map((p) => ({
            id: p.id,
            name: p.name,
            description: p.description ?? "",
            language: p.language,
          }))
          .sort(
            (a, b) =>
              allLanguages.indexOf(a.language) -
              allLanguages.indexOf(b.language)
          ),
        product_files: [...(productData.product_files ?? [])]
          .filter(file => !file.erp_managed)
          .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
          .map(file => ({
            _key: crypto.randomUUID(),
            id: file.id,
            name: file.name,
            edition: file.edition ?? "",
            url: file.url,
            suppressed: file.suppressed,
          })),
      });
      // Set ONLY here, and only after `product_files` has actually been written
      // into form state from a server response. This is the single fact
      // onSubmit trusts before it is allowed to full-replace the file list.
      setFilesHydratedForId(id);
    }
  }, [productData, form.reset, allLanguages, id]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty);
    }
  }, [form.formState.isDirty, onDirtyChange]);

  console.log("productData", productData);

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Simple Product</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            {/* An expired or non-staff token makes `product_files` throw, and
                because it is a nonNull list that kills the whole product query
                -- so the fields below are blank DEFAULTS, not the product. Say
                so, instead of presenting an editable-looking empty form. */}
            {productDataError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                <p className="font-medium">This product could not be loaded.</p>
                <p>
                  Your session may have expired — sign in again and reopen it.
                  The fields below are empty defaults, not this product&apos;s
                  data, so saving is disabled and nothing here can overwrite it.
                </p>
              </div>
            )}
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Product Name"
                description="The name of your product that will be displayed to customers."
                control={form.control}
                name={`product_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter product name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                description="Detailed description of your product including key features and benefits."
                control={form.control}
                name={`product_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Enter product description"
                        className="min-h-[100px] resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {id ? (
                <div className="border-t pt-4 mt-4">
                  <ContentBlocksManager
                    ref={managerRef}
                    owner={{ kind: "product", id }}
                    initialBlocks={productData?.content_blocks ?? []}
                    enableInlineCreate={false}
                    enableEditExisting
                    enablePermanentDelete={false}
                  />
                </div>
              ) : null}

              {/* Rendered only once the list has actually been loaded. An
                  editor bound to an empty list the admin never saw is a trap:
                  every row they add would look saved while onSubmit refuses to
                  send the list at all. */}
              {id ? (
                <div className="border-t pt-4 mt-4">
                  {productFilesHydrated ? (
                    <ProductFilesCard
                      form={form}
                      files={productData?.product_files ?? []}
                      refetchQueries={["GetSimpleProductDetails"]}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {loadingProductData
                        ? "Loading catalogue files…"
                        : "Catalogue files could not be loaded. Reload the product to edit them — saving now will leave the existing files unchanged."}
                    </p>
                  )}
                </div>
              ) : null}

              <div className="flex justify-end gap-2">
                <Button
                  type="submit"
                  disabled={loadingProductData || !!productDataError}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  );
}
